// THE PRODUCT'S DAY — the one source (TW-0086, 2026-09-08). Three generated byte-identical copies ride
// beside the code that reads them — apps/marketing/lib/day.ts, apps/app/lib/day.ts and, since TW-0222
// (2026-09-24), apps/form/src/day.ts — and a test on each side fails when a copy drifts from this file
// (the consent-sentences-es.json shape: edit HERE, then cp to all three).
//
// THE RULE, in Chris's words (2026-09-08, 00:20 EDT): "obviously i'm pleased to store the datetimes
// correctly in UTC. but when it comes to published dates, localization, whatever, either eastern or
// central makes sense to me" · "UTC as a default is ... off to me". Rows keep their UTC instants;
// every DAY a person READS — a published date, an export's filename, a "today" count, the hour a
// scheduled note goes out — is computed through the one zone named here. Machine comparisons stay on
// instants and never touch this file.
//
// The bug it exists to prevent has one shape: `new Date().toISOString().slice(0, 10)` and
// `new Date(y, m, d)` both read a day, and from 8pm Eastern on they read TOMORROW'S (on a server,
// which runs in UTC) or the READER'S rather than the product's. That is how a blog post dated 9/8
// went live at 8:08pm Eastern on 9/7, and how a shop's "September" export drops a waiver signed at
// 9pm Eastern on the 30th.

/** The business's clock: the ad account's zone, the blog's, the nudge hour's, the signing-rhythm
 *  report's. Change it HERE and every day a person reads follows. */
export const PRODUCT_TIMEZONE = 'America/New_York';

const DAY = new Intl.DateTimeFormat('en-US', {
  timeZone: PRODUCT_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const WALL = new Intl.DateTimeFormat('en-US', {
  timeZone: PRODUCT_TIMEZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const parts = (f: Intl.DateTimeFormat, at: Date): Record<string, string> =>
  Object.fromEntries(f.formatToParts(at).map((p) => [p.type, p.value]));

/** The calendar date (YYYY-MM-DD) of an instant, in the product's zone. 02:23Z on Sep 8 is Sep 7.
 *  An unusable instant is '' rather than a throw: `Intl.formatToParts` raises RangeError on an
 *  Invalid Date, and these helpers sit under list renders whose callers guard by returning an empty
 *  string. One bad row must not take down the screen it appears on. */
export function dayOf(at: Date | number = new Date()): string {
  const when = typeof at === 'number' ? new Date(at < 1e11 ? at * 1000 : at) : at;
  if (Number.isNaN(when.getTime())) return '';
  const p = parts(DAY, when);
  return `${p.year}-${p.month}-${p.day}`;
}

/** The product-zone YEAR of an instant. The zone has to decide this too: a render can carry
 *  `timeZone` and still be chosen by `getFullYear()`, which is the RUNTIME's year — so at 9pm
 *  Eastern on Dec 31 a server (UTC) drops the year from "Dec 31, 2025" and a browser keeps it. */
export const yearOf = (at: Date | number = new Date()): number => Number(dayOf(at).slice(0, 4)) || NaN;

/** Whole days between two product-zone day starts (epoch seconds), ROUNDED. The day the clocks
 *  change is 23 or 25 hours long, so `later - earlier === 86400` is false on it and a bare
 *  subtraction silently loses "Yesterday" twice a year. */
export const daysApart = (laterStart: number, earlierStart: number): number =>
  Math.round((laterStart - earlierStart) / 86400);

/** How far the product zone's wall clock sits from UTC at an instant, in ms — negative west of
 *  Greenwich. Read from the formatter rather than a table, so the DST rules are the platform's. */
export function zoneOffsetMs(at: Date): number {
  const p = parts(WALL, at);
  const wall = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return wall - Math.floor(at.getTime() / 1000) * 1000;
}

const isDay = (s: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(s);

/** The instant 00:00 on `date` in the product's zone, as UTC. Throws on anything but YYYY-MM-DD —
 *  a silently-wrong midnight is the failure this whole file exists to stop. */
export function startOfDay(date: string): Date {
  if (!isDay(date)) throw new RangeError(`startOfDay: not a YYYY-MM-DD date: ${JSON.stringify(date)}`);
  const [y, m, d] = date.split('-').map(Number);
  const midnight = Date.UTC(y, m - 1, d); // the wall-clock reading we want, taken as if it were UTC
  let t = midnight;
  // Two passes. For America/New_York one is provably enough — the two agree on all 16,801 days from
  // 1990 to 2035, DST days included, because midnight is never within an offset-change of itself.
  // The second is kept for zones where it is NOT (those with a sub-day-boundary transition), so
  // changing PRODUCT_TIMEZONE does not quietly need this file changed too. It is cheap and total.
  for (let i = 0; i < 2; i++) t = midnight - zoneOffsetMs(new Date(t));
  return new Date(t);
}

/** The LAST second of `date` in the product's zone, as UTC — the inclusive end of a day. A range
 *  that quietly ended at midnight would drop a day's rows without saying anything. */
export function endOfDay(date: string): Date {
  return new Date(startOfDay(dayAfter(date)).getTime() - 1000);
}

/** The calendar day `n` days before `date` (negative goes forward). Calendar arithmetic, never
 *  86400 seconds: the day the clocks change is 23 or 25 hours long. */
export function daysBefore(date: string, n: number): string {
  if (!isDay(date)) throw new RangeError(`daysBefore: not a YYYY-MM-DD date: ${JSON.stringify(date)}`);
  const [y, m, d] = date.split('-').map(Number);
  const shifted = new Date(Date.UTC(y, m - 1, d - n));
  const p = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const q = String(shifted.getUTCDate()).padStart(2, '0');
  return `${shifted.getUTCFullYear()}-${p}-${q}`;
}

const dayAfter = (date: string): string => daysBefore(date, -1);

/** Epoch SECONDS of 00:00 on the product-zone day containing `at`. The bucket boundary every
 *  "today" and "this week" count is measured from. NaN for an unusable instant, which is what the
 *  runtime-zone version it replaced returned — callers compare it and a NaN compares false. */
export const startOfDayFor = (at: Date | number = new Date()): number => {
  const day = dayOf(at);
  return day ? startOfDay(day).getTime() / 1000 : NaN;
};

/** Epoch SECONDS of the start of the product-zone day `n` days before the one containing `at`. */
export const startOfDaysAgo = (n: number, at: Date | number = new Date()): number =>
  startOfDay(daysBefore(dayOf(at), n)).getTime() / 1000;

/** The options every `toLocaleDateString`/`toLocaleTimeString` in a product surface must carry, so
 *  a server render (UTC) and the browser that hydrates over it agree on the day. */
export const IN_PRODUCT_ZONE = { timeZone: PRODUCT_TIMEZONE } as const;

/** "October 1, 2026" for "2026-10-01", the way a person reads a day (day.py's words_for_day, the long form):
 *  the day a state's rule starts, printed on a state page and in Settings (TW-0222). The day is read as a
 *  calendar date and never as an instant, so no zone can move it. Anything that is not YYYY-MM-DD comes
 *  back as it came. */
export function wordsForDay(date: string): string {
  if (!isDay(date)) return date;
  const [y, m, d] = date.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, d))
  );
}
